// @flow
import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import CustomEditor from '../CustomEditor/CustomEditor';
import {Grid, Hidden, makeStyles, Snackbar} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {colors} from '../../../../../assets/styles/colors';
import {ImageButton} from '../ImageButton/ImageButton';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {getImageUrl} from '../../../../../utils/image_provider';
import {useIsFeatureInTier} from '../../../../../utils/component';

import type {SaveLegalRequest} from '../../api';
import type {AboutUs} from '../../duck';
import CropModal from '../../../../../elements/CropModal';
import type {ImageToCrop} from '../../../../../elements/CropModal';
import {useMap} from '../../../../../utils/react/useMap';
import {IMAGES_ASPECT_RATIO_LAYOUT_1, fillArray} from './constants';

type Props = {
  disabled?: boolean,
  aboutUs?: AboutUs,
  handleSubmitRequest: (data: SaveLegalRequest) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    position: 'relative',
    padding: theme.spacing(2),
    backgroundColor: colors.grey200,
    borderRadius: '8px',
  },
  singleRow: {
    margin: theme.spacing(1, 0),
  },
  hiddenBlock: {
    display: 'flex',
    flexDirection: 'row-reverse',
    height: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 0,
      marginBottom: theme.spacing(4),
    },
  },
  buttonSave: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #D4D4DD',
    padding: theme.spacing(1, 3),
    [theme.breakpoints.up('md')]: {
      backgroundColor: '#FFFFFF',
    },
  },
  wrapImageLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  bottomBar: {
    zIndex: 1200,
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
  },
  bottomBarContent: {
    maxWidth: 1300,
    padding: theme.spacing(2, 4),
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
}));

const TOTAL_IMAGES = 6;

const INITIAL_IMAGE_COLLECTION = new Array(TOTAL_IMAGES)
  .fill(null)
  .reduce((acc, _, idx) => {
    acc[idx] = null;
    return acc;
  }, {});

export default function FirstLayout(props: Props) {
  const {disabled = false, aboutUs, handleSubmitRequest} = props;
  const classes = useStyles();
  const hasAboutUsInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const hasAboutUsInformationFeatureDisabled = !hasAboutUsInformationFeature;

  const [inputFocused, setInputFocused] = useState(0);

  const [title, setTitle] = useState('');
  const [description1, setDescription1] = useState('');
  const [description2, setDescription2] = useState('');
  const [description3, setDescription3] = useState('');

  const [image4Label, setImage4label] = useState('');
  const [image5Label, setImage5label] = useState('');
  const [image6Label, setImage6label] = useState('');

  const [layoutImage, setLayoutImage] = useState<ImageToCrop | null>(null);

  const currentImageIndex = useRef<number>(0);
  const layoutResultMap = useMap<number, ImageToCrop | null>(
    INITIAL_IMAGE_COLLECTION
  );

  const [savedImages, setSavedImages] = useState<string[]>([]);

  const [imagesError, setImagesError] = useState('');

  const handleInputFocused = useCallback(
    (idx: number) => setInputFocused(idx),
    []
  );

  const handleTitleChange = useCallback((text: string) => {
    setTitle(text);
  }, []);

  const handleDescriptionChange = useCallback((desc: string, idx: number) => {
    switch (idx) {
      case 1:
        setDescription1(desc);
        break;
      case 2:
        setDescription2(desc);
        break;
      case 3:
        setDescription3(desc);
        break;
      case 4:
        setImage4label(desc);
        break;
      case 5:
        setImage5label(desc);
        break;
      case 6:
        setImage6label(desc);
        break;
      default:
        return;
    }
  }, []);

  const handleChangeImage = useCallback(
    async (event: SyntheticInputEvent<HTMLInputElement>, idx: number) => {
      const file = event.target.files[0];

      if (!file) return;

      const layoutImage = {
        file,
        preview: URL.createObjectURL(file),
      };

      setLayoutImage(layoutImage);

      currentImageIndex.current = idx;
    },
    []
  );

  const handleRemoveImage = (idx: number) => {
    layoutResultMap.set(idx, null);
    setSavedImages(prev => fillArray([...prev], idx, ''));
  };

  const handleSaveAboutUs = () => {
    const resultImagesCropped = Array.from(layoutResultMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([_, value]) => value);

    const getCompletedImages = () => {
      for (let i = 0; i < TOTAL_IMAGES; i++) {
        if (resultImagesCropped[i] || savedImages[i]) {
          continue;
        }
        return false;
      }
      return true;
    };

    const completedImages = getCompletedImages();

    if (!completedImages) {
      setImagesError('Debes escoger las imágenes que irán en la plantilla');
    } else {
      const imagesFiles = resultImagesCropped.map(result => result?.file);
      let arrFilledFile = [];
      resultImagesCropped.forEach((file, idx) => {
        if (file)
          arrFilledFile.push({
            name: `image${idx + 1}`,
            id: `image${idx + 1}Id`,
          });
      });
      const legalRequest = {
        enabled: true,
        storeAboutInformationType: 'LARGE',
        title,
        description1,
        description2,
        description3,
        image1: imagesFiles[0],
        image2: imagesFiles[1],
        image3: imagesFiles[2],
        image4: imagesFiles[3],
        image5: imagesFiles[4],
        image6: imagesFiles[5],
        image4Label,
        image5Label,
        image6Label,
        arrFilledFile,
      };

      handleSubmitRequest({legalRequest});
    }
  };

  useEffect(() => {
    if (!disabled && aboutUs) {
      const {
        title = '',
        description1 = '',
        description2 = '',
        description3 = '',
        image4Label = '',
        image5Label = '',
        image6Label = '',
        image1Id,
        image2Id,
        image3Id,
        image4Id,
        image5Id,
        image6Id,
      } = aboutUs;
      setTitle(title);
      setDescription1(description1);
      setDescription2(description2);
      setDescription3(description3);
      setImage4label(image4Label);
      setImage5label(image5Label);
      setImage6label(image6Label);

      if (image1Id) {
        setSavedImages(prev => fillArray([...prev], 0, getImageUrl(image1Id)));
      }
      if (image2Id) {
        setSavedImages(prev => fillArray([...prev], 1, getImageUrl(image2Id)));
      }
      if (image3Id) {
        setSavedImages(prev => fillArray([...prev], 2, getImageUrl(image3Id)));
      }
      if (image4Id) {
        setSavedImages(prev => fillArray([...prev], 3, getImageUrl(image4Id)));
      }
      if (image5Id) {
        setSavedImages(prev => fillArray([...prev], 4, getImageUrl(image5Id)));
      }
      if (image6Id) {
        setSavedImages(prev => fillArray([...prev], 5, getImageUrl(image6Id)));
      }
    }
  }, [aboutUs, disabled]);

  const layoutImagesPreview = useMemo(() => {
    return layoutResultMap
      .values()
      .map((result, index) => (result ? result.preview : savedImages[index]));
  }, [layoutResultMap, savedImages]);

  return (
    <div className={classes.root} data-color-mode="light">
      {layoutImage && (
        <CropModal
          open
          images={[
            {
              file: layoutImage.file,
              preview: layoutImage.preview,
            },
          ]}
          onClose={() => {
            setLayoutImage(null);
          }}
          aspect={
            IMAGES_ASPECT_RATIO_LAYOUT_1[currentImageIndex.current]?.aspectRatio
          }
          compressOptions={{
            maxWidth: 1600,
            quality: 1,
            maxHeight:
              1600 /
                IMAGES_ASPECT_RATIO_LAYOUT_1[currentImageIndex.current]
                  ?.aspectRatio || 1,
          }}
          onSubmit={results => {
            console.log({
              original: layoutImage.file,
              compressed: results[0].file,
            });
            const currentIndex = currentImageIndex.current;

            layoutResultMap.set(currentIndex, results[0]);
            // setLayoutImageResult(results);
            setLayoutImage(null);
          }}
        />
      )}

      <Snackbar
        open={!!imagesError}
        autoHideDuration={4000}
        onClose={() => setImagesError('')}
      >
        <Alert severity="error" onClose={() => setImagesError('')}>
          {imagesError}
        </Alert>
      </Snackbar>
      <Hidden smDown>
        <div className={classes.hiddenBlock} id="container-toolbar" />
        {!hasAboutUsInformationFeatureDisabled && (
          <div className={classes.bottomBar}>
            <div className={classes.bottomBarContent}>
              <CustomButton
                variant="contained"
                className={classes.bottomBarButton}
                onClick={handleSaveAboutUs}
                disabled={disabled}
              >
                Guardar cambios
              </CustomButton>
            </div>
          </div>
        )}
      </Hidden>
      {!hasAboutUsInformationFeatureDisabled && (
        <Hidden mdUp>
          <div className={classes.hiddenBlock} id="container-toolbar">
            <CustomButton
              disabled={disabled}
              className={classes.buttonSave}
              onClick={handleSaveAboutUs}
            >
              Guardar
            </CustomButton>
          </div>
        </Hidden>
      )}

      <div className={classes.singleRow}>
        <CustomEditor
          disabled={disabled || hasAboutUsInformationFeatureDisabled}
          stickyToolbar
          idx={0}
          selected={inputFocused === 0}
          height={56}
          placeholder="Titulo de página"
          handleInputFocused={handleInputFocused}
          text={title}
          onChange={handleTitleChange}
        />
      </div>
      <div className={classes.singleRow}>
        <CustomEditor
          disabled={disabled || hasAboutUsInformationFeatureDisabled}
          stickyToolbar
          idx={1}
          height={112}
          selected={inputFocused === 1}
          handleInputFocused={handleInputFocused}
          text={description1}
          onChange={handleDescriptionChange}
        />
      </div>
      <div
        className={classes.singleRow}
        style={{
          aspectRatio: IMAGES_ASPECT_RATIO_LAYOUT_1[0].aspectRatio,
        }}
      >
        <ImageButton
          disabled={disabled || hasAboutUsInformationFeatureDisabled}
          idx={0}
          maxHeight={340}
          images={layoutImagesPreview}
          handleRemoveImage={handleRemoveImage}
          handleChangeImage={handleChangeImage}
          placeholder="Agregar imagen (928px por 340px)"
          expand
        />
      </div>

      <div className={classes.singleRow}>
        <CustomEditor
          disabled={disabled || hasAboutUsInformationFeatureDisabled}
          stickyToolbar
          idx={2}
          height={112}
          selected={inputFocused === 2}
          handleInputFocused={handleInputFocused}
          text={description2}
          onChange={handleDescriptionChange}
        />
      </div>
      <div className={classes.singleRow}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <ImageButton
              disabled={disabled || hasAboutUsInformationFeatureDisabled}
              idx={1}
              maxHeight={340}
              images={layoutImagesPreview}
              handleRemoveImage={handleRemoveImage}
              handleChangeImage={handleChangeImage}
              placeholder="Agregar imagen (452px por 340px)"
              expand
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImageButton
              disabled={disabled || hasAboutUsInformationFeatureDisabled}
              idx={2}
              maxHeight={340}
              images={layoutImagesPreview}
              handleRemoveImage={handleRemoveImage}
              handleChangeImage={handleChangeImage}
              placeholder="Agregar imagen (452px por 340px)"
              expand
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.singleRow}>
        <CustomEditor
          disabled={disabled || hasAboutUsInformationFeatureDisabled}
          stickyToolbar
          idx={3}
          height={112}
          selected={inputFocused === 3}
          handleInputFocused={handleInputFocused}
          text={description3}
          onChange={handleDescriptionChange}
        />
      </div>
      <div className={classes.singleRow}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ImageButton
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  expand
                  idx={3}
                  maxHeight={288}
                  images={layoutImagesPreview}
                  handleRemoveImage={handleRemoveImage}
                  handleChangeImage={handleChangeImage}
                  placeholder="Agregar imagen (288px por 288px)"
                />
              </Grid>
              <Grid item xs={12}>
                <CustomEditor
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  stickyToolbar
                  height={56}
                  idx={4}
                  selected={inputFocused === 4}
                  handleInputFocused={handleInputFocused}
                  text={image4Label}
                  onChange={handleDescriptionChange}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ImageButton
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  expand
                  idx={4}
                  maxHeight={288}
                  images={layoutImagesPreview}
                  handleRemoveImage={handleRemoveImage}
                  handleChangeImage={handleChangeImage}
                  placeholder="Agregar imagen (288px por 288px)"
                />
              </Grid>
              <Grid item xs={12}>
                <CustomEditor
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  stickyToolbar
                  height={56}
                  idx={5}
                  selected={inputFocused === 5}
                  handleInputFocused={handleInputFocused}
                  text={image5Label}
                  onChange={handleDescriptionChange}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ImageButton
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  expand
                  idx={5}
                  maxHeight={288}
                  images={layoutImagesPreview}
                  handleRemoveImage={handleRemoveImage}
                  handleChangeImage={handleChangeImage}
                  placeholder="Agregar imagen (288px por 288px)"
                />
              </Grid>
              <Grid item xs={12}>
                <CustomEditor
                  disabled={disabled || hasAboutUsInformationFeatureDisabled}
                  stickyToolbar
                  height={56}
                  idx={6}
                  selected={inputFocused === 6}
                  handleInputFocused={handleInputFocused}
                  text={image6Label}
                  onChange={handleDescriptionChange}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

FirstLayout.propTypes = {
  disabled: PropTypes.bool,
  aboutUs: PropTypes.object,
  handleSubmitRequest: PropTypes.func.isRequired,
};
